import { Link } from '@tanstack/react-router';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, type Zone } from '@up2d8/ui';
import {
  AdminIcon,
  AdminPage,
  Card,
  Empty,
  ProductBrand,
  ProductWordmark,
  initials,
} from '../../modules/admin-kit';
import { GlobalSearch } from '../../shared/global-search';
import { pageBlock } from '../../shared/page-access';
import { TenantMenu } from '../../shared/tenant-switcher';
import { useTenantFeatures } from '../../shared/use-tenant-features';
import { useSession } from '../../shared/session';
import { useActiveBrand, useProductName } from '../../shared/theme';
import { useTenantTheme } from '../../shared/use-tenant-theme';
import { AdminAccountMenu } from './admin-account-menu';
import { adminNav, platformNav, useAdminCounts, type AdminNavItem } from './admin-nav';

interface ZoneLayoutProps {
  zone: Zone;
  /**
   * De i18n-sleutel van de paginatitel, niet de titel zelf.
   *
   * De titels stonden als vaste Nederlandse tekst in `router.tsx` (Extra 2, punt 12). Een
   * Franstalige beheerder zag dus een Franse interface met "Gegevensvelden" erboven. De sleutel
   * en niet de tekst, zodat de vertaling op één plek staat en de router er niets van hoeft te
   * weten.
   */
  titleKey: string;
  activeKey: string;
  /**
   * De pagina heeft een eigen paginakop (`AdminPage` + `PageHead` uit de admin-kit, BHR-0).
   *
   * In het nieuwe ontwerp staat de titel twee keer: klein (18 px) in de kopbalk en groot (24 px)
   * boven de inhoud. Er hoort maar één `h1` te zijn. Zonder deze vlag is de titel in de kopbalk de
   * `h1` - zo blijven de schermen werken die nog niet herontworpen zijn. Met de vlag is hij gewone
   * tekst en is de `PageHead` van de pagina de `h1`.
   */
  ownHeading?: boolean;
  children: ReactNode;
}

/**
 * De schil van `/admin` (beheer) en `/master` (platformbeheer), sinds BHR-0 volgens het nieuwe
 * ontwerp (basis-spec A §3, HTML in B.3): navy zijbalk van 240 met merk en bedrijfswisselaar, Start
 * los en daaronder de groepen, een kopbalk met titel, zoekveld en avatarmenu.
 *
 * `data-zone` op de buitenste laag bepaalt de tokenset en schermt `admin.css` af van de app-zone.
 * De medewerkerszone (`/app`) heeft een eigen schil (`DeskShell`) en komt hier niet langs.
 */
export function ZoneLayout({ zone, titleKey, activeKey, ownHeading, children }: ZoneLayoutProps) {
  const { t } = useTranslation();
  const session = useSession();
  const isPlatform = zone === 'platform';

  // De huisstijl van de klant, ook in de beheerzone (Stap 43): naam en logo van het bedrijf.
  const theme = useTenantTheme();
  const productName = useProductName();

  const counts = useAdminCounts(session, !isPlatform);
  const { isOn } = useTenantFeatures();
  const nav = isPlatform ? platformNav(t, session.has) : adminNav(session, t, counts, isOn);

  /*
    Een pagina vraagt hetzelfde als haar menu-item (FBK-2). Wie het recht niet heeft, kreeg vroeger
    een scherm dat half laadde met "Ophalen is niet gelukt"; nu staat er waarom. Staat het item er
    wel bij alle vlaggen aan, dan mist niet het recht maar staat de functie uit. Een scherm over
    jezelf (Wachtwoord wijzigen) staat niet in het menu en is altijd open: zie `pageBlock`. Pas na
    het laden van de rechten, anders flitst "Geen toegang" bij elke eerste laadbeurt.
  */
  const blocked = (() => {
    if (isPlatform || !session.permissionsReady) return null;
    const keys = (shown: typeof nav) =>
      new Set([...shown.top, ...shown.groups.flatMap((group) => group.items)].map((i) => i.key));
    return pageBlock({
      activeKey,
      shown: keys(nav),
      withAllFeatures: keys(adminNav(session, t, {})),
    });
  })();

  // Alleen de groep van het actieve scherm staat open; de andere klap je zelf open (A §3).
  const activeGroup = nav.groups.find((group) =>
    group.items.some((item) => item.key === activeKey),
  )?.key;
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(activeGroup ? [activeGroup] : []),
  );
  useEffect(() => {
    if (activeGroup) setOpenGroups((current) => new Set(current).add(activeGroup));
  }, [activeGroup]);

  const toggle = (key: string) =>
    setOpenGroups((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const tenantName = theme?.displayName || productName;
  // Op de donkere zijbalk het logo voor een donkere ondergrond, zoals de bureauschil (Extra 1).
  const logoUrl = theme?.logoDarkUrl ?? theme?.logoUrl ?? null;
  const title = t(titleKey);
  const TitleTag = ownHeading ? 'div' : 'h1';

  return (
    <div data-zone={zone}>
      <div className="app">
        <aside className="sidebar a-sidebar">
          {/*
            Bovenaan het merk van wie de pagina toebehoort (FBK-3, ADR-0091): in Master het product,
            in het beheer van een bedrijf het logo van dat bedrijf - of zijn naam, zonder logo. Het
            productmerk staat in het beheer klein onderaan.
          */}
          <div className="sidebar-brand">
            {isPlatform ? (
              <ProductBrand variant="sidebar" />
            ) : (
              <TenantBrand name={tenantName} logoUrl={logoUrl} />
            )}
          </div>

          {isPlatform ? (
            <div className="sidebar-org">
              <div
                className="sidebar-org-icon"
                style={{
                  background: 'var(--brand-orange-tint)',
                  color: 'var(--brand-orange-deep)',
                }}
                aria-hidden="true"
              >
                {initials(productName)}
              </div>
              <div className="sidebar-org-info">
                <div className="sidebar-org-name">{t('beheer.shell.platformName')}</div>
                <div className="sidebar-org-role">{t('beheer.shell.platformSub')}</div>
              </div>
            </div>
          ) : (
            /*
              De bedrijfswisselaar: de bestaande `TenantMenu` (ACC-1), met de klassen van de mockup.
              Wie maar één bedrijf heeft, krijgt hetzelfde blok zonder knop en zonder pijltje.
            */
            <TenantMenu
              zone="admin"
              tone="dark"
              buttonClassName="sidebar-org"
              chevron={
                <AdminIcon
                  name="chevronDown"
                  size={14}
                  className="sidebar-org-chevron"
                  style={{ color: 'rgba(255,255,255,0.6)', marginLeft: 'auto', flexShrink: 0 }}
                />
              }
              single={(content) => <div className="sidebar-org">{content}</div>}
            >
              <div className="sidebar-org-icon" aria-hidden="true">
                {initials(tenantName)}
              </div>
              <div className="sidebar-org-info">
                <div className="sidebar-org-name" data-testid="sidebar-brand">
                  {tenantName}
                </div>
                {theme?.legalName ? (
                  <div className="sidebar-org-role">{theme.legalName}</div>
                ) : null}
              </div>
            </TenantMenu>
          )}

          <nav className="sidebar-nav" aria-label={t('beheer.shell.nav')}>
            {nav.top.map((item) => (
              <NavLink key={item.key} item={item} active={item.key === activeKey} />
            ))}
            {nav.groups.map((group) => (
              <NavGroup
                key={group.key}
                label={group.label}
                open={openGroups.has(group.key)}
                openCount={group.items.reduce((sum, item) => sum + (item.count ?? 0), 0)}
                onToggle={() => toggle(group.key)}
              >
                {group.items.map((item) => (
                  <NavLink key={item.key} item={item} active={item.key === activeKey} />
                ))}
              </NavGroup>
            ))}
          </nav>

          {isPlatform ? null : <ProductMark />}
        </aside>

        <header className="header a-header">
          <TitleTag className="a-header-title">{title}</TitleTag>
          {/* Zoeken in de kopbalk en niet op een eigen pagina (Stap 34d). Alleen in de beheerzone
              van een bedrijf: een platformbeheerder heeft geen berichten, collega's of documenten. */}
          {!isPlatform ? (
            <div className="a-header-search">
              <GlobalSearch zone="admin" />
            </div>
          ) : null}
          <AdminAccountMenu
            zone={isPlatform ? 'platform' : 'admin'}
            roleLine={
              isPlatform
                ? t('beheer.shell.rolePlatform')
                : t('beheer.shell.roleAdmin', { tenant: tenantName })
            }
          />
        </header>

        <main className="main a-main">
          {blocked ? <NoAccess reason={blocked} page={title} /> : children}
        </main>
      </div>
    </div>
  );
}

/**
 * Het merk van het bedrijf bovenaan de zijbalk (FBK-3). Een logo draagt zijn eigen naam en staat
 * alleen; zonder logo (of als het niet laadt) een tegel met initialen en de naam.
 */
function TenantBrand({ name, logoUrl }: { name: string; logoUrl: string | null }) {
  const [broken, setBroken] = useState<string | null>(null);
  if (logoUrl && broken !== logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name}
        className="sidebar-brand-logo"
        data-testid="sidebar-logo"
        onError={() => setBroken(logoUrl)}
      />
    );
  }
  return (
    <>
      <div className="sidebar-brand-shield" aria-hidden="true">
        {initials(name)}
      </div>
      <div className="sidebar-brand-text" data-testid="sidebar-tenant-name">
        {name}
      </div>
    </>
  );
}

/**
 * Het productmerk klein onderaan de zijbalk van het beheer: van wie de software is (UP2D8 of een
 * wederverkoper, ADR-0084). De beheerder van een klant heeft het product gekocht en mag zien van
 * wie; in de medewerkerszone staat het niet (ADR-0071).
 */
function ProductMark() {
  const { t } = useTranslation();
  const { productName, productLogoDarkUrl } = useActiveBrand();
  return (
    <div className="sidebar-product" data-testid="sidebar-product">
      <span>{t('beheer.shell.poweredBy')}</span>
      {productLogoDarkUrl ? (
        <img src={productLogoDarkUrl} alt={productName} />
      ) : (
        <strong>
          <ProductWordmark />
        </strong>
      )}
    </div>
  );
}

/** Geen recht op deze pagina, of de functie staat uit (FBK-2). */
function NoAccess({ reason, page }: { reason: 'noRight' | 'featureOff'; page: string }) {
  const { t } = useTranslation();
  return (
    <AdminPage>
      <Card>
        <Empty
          testId="no-access"
          icon={<AdminIcon name="lock" />}
          title={t('errors.noAccessTitle')}
          text={
            reason === 'noRight'
              ? t('errors.noAccessText', { page })
              : t('errors.featureOffText', { page })
          }
        />
      </Card>
    </AdminPage>
  );
}

/**
 * Een groepskop (`.a-group`) en zijn items. Ingeklapt toont hij "· 3 open" als er tellers zijn.
 * De items blijven in de DOM met `data-closed`: onder 900 toont de iconenbalk ze allemaal.
 */
function NavGroup({
  label,
  open,
  openCount,
  onToggle,
  children,
}: {
  label: string;
  open: boolean;
  openCount: number;
  onToggle: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <>
      <button
        type="button"
        className={cn('a-group', open ? 'open' : 'closed')}
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
      >
        {label}
        {openCount > 0 ? (
          <span className="a-group-count">{t('beheer.shell.groupOpen', { count: openCount })}</span>
        ) : null}
        <AdminIcon name="chevronDown" size={14} />
      </button>
      <div className="a-group-items" id={id} data-closed={open ? undefined : ''}>
        {children}
      </div>
    </>
  );
}

/**
 * Een item in de zijbalk. De teller staat als badge in beeld, maar niet in de naam van de link:
 * "Meldingen" blijft "Meldingen" voor een schermlezer en voor de tests; "3 open" komt als
 * beschrijving.
 */
function NavLink({ item, active }: { item: AdminNavItem; active: boolean }) {
  const { t } = useTranslation();
  const countId = useId();
  const count = item.count && item.count > 0 ? item.count : null;
  return (
    <>
      <Link
        to={item.href}
        className={cn('sidebar-item', active && 'active')}
        /*
          Welk item oplicht, beslist `activeKey`, niet de router. Zonder deze twee regels zette
          TanStack zelf `active` op Start (`/admin`) op elke pagina eronder.
        */
        activeOptions={{ exact: true }}
        activeProps={{ className: '' }}
        aria-current={active ? 'page' : undefined}
        aria-describedby={count ? countId : undefined}
        data-testid={`admin-nav-${item.key}`}
      >
        <span className="sidebar-item-icon">
          <AdminIcon name={item.icon} size={18} />
        </span>
        <span className="sidebar-item-label">{item.label}</span>
        {count ? (
          <span className="sidebar-item-badge" aria-hidden="true">
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </Link>
      {count ? (
        <span id={countId} hidden>
          {t('beheer.shell.itemOpen', { count })}
        </span>
      ) : null}
    </>
  );
}
